import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createEngagement,
  lookupCvr,
  findTenantByEmailDomain,
  findTenantByName,
  isGenericDomain,
  requestTenantAccess,
  inviteTenant,
} from './grcClient.js';
import { GRC_FRAMEWORKS } from './grcFrameworks.js';

const STEPS = ['Company', 'Engagement', 'Framework'];

function StepDots({ current }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
      {STEPS.map((label, i) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: i <= current ? 'var(--accent)' : 'var(--border)',
              transition: 'background 0.2s',
            }}
          />
          {i < STEPS.length - 1 && (
            <div
              style={{
                width: 20,
                height: 1,
                background: i < current ? 'var(--accent)' : 'var(--border)',
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function GrcNewClientModal({ onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Step 0: company details
  const [cvrNumber, setCvrNumber] = useState('');
  const [cvrLoading, setCvrLoading] = useState(false);
  const [cvrError, setCvrError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [tenantSuggest, setTenantSuggest] = useState(null); // { name, slug }
  const [tryggrSlug, setTryggrSlug] = useState('');
  const [domainChecked, setDomainChecked] = useState(false);
  const [tenantNotFound, setTenantNotFound] = useState(false); // shown after both checks return nothing
  const [inviteMode, setInviteMode] = useState(false); // auditor chose "Invite to Tryggr"

  // Step 1: engagement details
  const [engType, setEngType] = useState('');
  const [reference, setReference] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  // Step 2: framework
  const [frameworkId, setFrameworkId] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── CVR lookup ────────────────────────────────────────────────────────────
  const handleCvrLookup = useCallback(async () => {
    if (!cvrNumber.trim()) return;
    setCvrLoading(true);
    setCvrError('');
    setTenantNotFound(false);
    try {
      const result = await lookupCvr(cvrNumber.trim());
      if (!result) {
        setCvrError('Company not found in registry');
        return;
      }
      if (result.name) setCompanyName(result.name);

      let found = null;
      // Try email domain first
      if (result.email && !isGenericDomain(result.email)) {
        if (!contactEmail) setContactEmail(result.email);
        found = await findTenantByEmailDomain(result.email);
      }
      // Fallback: name-based search
      if (!found && result.name) {
        found = await findTenantByName(result.name);
      }

      if (found) {
        setTenantSuggest(found);
      } else {
        setTenantNotFound(true);
      }
    } catch {
      setCvrError('Lookup failed — please enter company name manually');
    } finally {
      setCvrLoading(false);
    }
  }, [cvrNumber, contactEmail]);

  // ── Domain check on contact email blur ───────────────────────────────────
  const handleEmailBlur = useCallback(async () => {
    if (!contactEmail || isGenericDomain(contactEmail) || domainChecked) return;
    setDomainChecked(true);
    try {
      let found = await findTenantByEmailDomain(contactEmail);
      if (!found && companyName) found = await findTenantByName(companyName);
      if (found) {
        if (!tenantSuggest) setTenantSuggest(found);
        setTenantNotFound(false);
      } else if (companyName) {
        setTenantNotFound(true);
      }
    } catch {
      /* silent */
    }
  }, [contactEmail, domainChecked, tenantSuggest, companyName]);

  // ── Step validation ───────────────────────────────────────────────────────
  const step0Valid = companyName.trim().length > 0;
  const step1Valid = engType.trim().length > 0;
  const step2Valid = frameworkId.length > 0;

  const canNext = step === 0 ? step0Valid : step === 1 ? step1Valid : step2Valid;

  // ── Final submit ──────────────────────────────────────────────────────────
  const handleCreate = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const result = await createEngagement({
        clientName: companyName.trim(),
        engagementType: engType.trim(),
        reference: reference.trim() || undefined,
        periodStart: periodStart || undefined,
        periodEnd: periodEnd || undefined,
        frameworkId: frameworkId === 'other' ? null : frameworkId,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        cvrNumber: cvrNumber.trim() || undefined,
        tryggrSlug: tryggrSlug.trim() || undefined,
        pendingInvite: inviteMode && !tryggrSlug.trim() ? true : undefined,
      });

      const newEngagementId = result.engagement?.id;

      // If linked to an existing Tryggr workspace, automatically send an
      // access request so the tenant admin can accept from their workspace.
      if (tryggrSlug.trim() && newEngagementId) {
        try {
          await requestTenantAccess({ engagementId: newEngagementId });
        } catch {
          // Non-fatal — the auditor can request access manually from the engagement page.
        }
      }

      // If in invite mode and no existing workspace was linked, spin up a
      // new free workspace and send the welcome email.
      if (inviteMode && !tryggrSlug.trim() && newEngagementId && contactEmail.trim()) {
        const nameParts = contactName.trim().split(' ');
        try {
          await inviteTenant(newEngagementId, {
            adminEmail: contactEmail.trim(),
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
          });
        } catch {
          // Non-fatal — auditor can send invite from the engagement page.
        }
      }

      navigate(`/clients/${encodeURIComponent(companyName.trim())}`);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to create engagement');
      setSaving(false);
    }
  }, [
    companyName,
    engType,
    reference,
    periodStart,
    periodEnd,
    frameworkId,
    contactName,
    contactEmail,
    cvrNumber,
    tryggrSlug,
    inviteMode,
    navigate,
    onClose,
  ]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 700,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: 520, padding: 32, position: 'relative' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--fg3)',
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>
          New client
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 20 }}>{STEPS[step]}</div>

        <StepDots current={step} />

        {/* ── Step 0: Company ── */}
        {step === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* CVR */}
            <div>
              <label
                style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
              >
                CVR / Company ID
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="input"
                  placeholder="e.g. 12345678"
                  value={cvrNumber}
                  onChange={(e) => {
                    setCvrNumber(e.target.value);
                    setCvrError('');
                    setTenantNotFound(false);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleCvrLookup()}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleCvrLookup}
                  disabled={!cvrNumber.trim() || cvrLoading}
                >
                  {cvrLoading ? 'Looking up…' : 'Look up'}
                </button>
              </div>
              {cvrError && (
                <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>{cvrError}</div>
              )}
            </div>

            {/* Company name */}
            <div>
              <label
                style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
              >
                Company name <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                className="input"
                placeholder="Acme A/S"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            {/* Contact name */}
            <div>
              <label
                style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
              >
                Contact name
              </label>
              <input
                className="input"
                placeholder="Jane Smith"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>

            {/* Contact email */}
            <div>
              <label
                style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
              >
                Contact email
              </label>
              <input
                className="input"
                type="email"
                placeholder="jane@acme.dk"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  setDomainChecked(false);
                  setTenantSuggest(null);
                  setTenantNotFound(false);
                }}
                onBlur={handleEmailBlur}
              />
            </div>

            {/* Tenant suggestion banner */}
            {tenantSuggest && !tryggrSlug && (
              <div
                className="card"
                style={{
                  padding: '12px 14px',
                  background: 'var(--accent-bg)',
                  borderColor: 'var(--accent)',
                }}
              >
                <div style={{ fontSize: 12, color: 'var(--fg)', marginBottom: 8 }}>
                  We know a workspace called <strong>{tenantSuggest.name}</strong> — could it be
                  them?
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setTryggrSlug(tenantSuggest.slug)}
                  >
                    Yes, link it
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setTenantSuggest(null)}>
                    No, different company
                  </button>
                </div>
              </div>
            )}

            {/* "Not found" notice — shown when CVR+name search returned nothing */}
            {tenantNotFound && !tryggrSlug && !inviteMode && (
              <div
                className="card"
                style={{
                  padding: '12px 14px',
                  borderColor: 'var(--warn)',
                  background: 'rgba(251,191,36,0.06)',
                }}
              >
                <div style={{ fontSize: 12, color: 'var(--fg)', marginBottom: 10 }}>
                  No Tryggr workspace found for this company.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button className="btn btn-sm btn-ghost" onClick={() => setTenantNotFound(false)}>
                    Continue without linking
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      setCvrNumber('');
                      setCvrError('');
                      setTenantNotFound(false);
                      setCompanyName('');
                    }}
                  >
                    Check CVR
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setTenantNotFound(false);
                      setInviteMode(true);
                    }}
                  >
                    Invite to Tryggr
                  </button>
                </div>
              </div>
            )}

            {/* Invite mode banner */}
            {inviteMode && !tryggrSlug && (
              <div
                className="card"
                style={{
                  padding: '12px 14px',
                  borderColor: 'var(--accent)',
                  background: 'var(--accent-bg)',
                }}
              >
                <div style={{ fontSize: 12, color: 'var(--fg)', marginBottom: 4, fontWeight: 600 }}>
                  Invite to Tryggr
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg2)', marginBottom: 8 }}>
                  After creating this engagement the client will receive an invitation to set up
                  their free Tryggr workspace. You can track delivery from the engagement page.
                </div>
                <button
                  className="btn btn-sm btn-ghost"
                  style={{ fontSize: 11 }}
                  onClick={() => setInviteMode(false)}
                >
                  Cancel invite
                </button>
              </div>
            )}

            {tryggrSlug && (
              <div style={{ fontSize: 12, color: 'var(--accent)' }}>
                Linked to Tryggr workspace: <strong>{tryggrSlug}</strong>
                <button
                  onClick={() => {
                    setTryggrSlug('');
                    setTenantSuggest(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--fg3)',
                    marginLeft: 8,
                    fontSize: 11,
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 1: Engagement details ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label
                style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
              >
                Engagement type <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                className="input"
                placeholder="e.g. ISO 27001 Certification Audit"
                value={engType}
                onChange={(e) => setEngType(e.target.value)}
              />
            </div>

            <div>
              <label
                style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
              >
                Reference
              </label>
              <input
                className="input"
                placeholder="e.g. ENG-2025-001"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label
                  style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
                >
                  Period start
                </label>
                <input
                  className="input"
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                />
              </div>
              <div>
                <label
                  style={{ fontSize: 12, color: 'var(--fg3)', display: 'block', marginBottom: 4 }}
                >
                  Period end
                </label>
                <input
                  className="input"
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Framework ── */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 12 }}>
              Select the framework this engagement targets.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {GRC_FRAMEWORKS.map((fw) => (
                <button
                  key={fw.id}
                  onClick={() => setFrameworkId(fw.id)}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: `1px solid ${frameworkId === fw.id ? 'var(--accent)' : 'var(--border)'}`,
                    background: frameworkId === fw.id ? 'var(--accent-bg)' : 'var(--bg2)',
                    color: 'var(--fg)',
                    fontWeight: frameworkId === fw.id ? 600 : 400,
                    fontSize: 13,
                  }}
                >
                  {fw.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => (step === 0 ? onClose?.() : setStep((s) => s - 1))}
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              className="btn btn-sm btn-primary"
              disabled={!canNext}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </button>
          ) : (
            <button
              className="btn btn-sm btn-primary"
              disabled={!canNext || saving}
              onClick={handleCreate}
            >
              {saving ? 'Creating…' : 'Create client'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
